import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface PresentationModeContextType {
  enabled: boolean;
  enable: () => void;
  disable: () => void;
  toggle: () => void;
}

const PresentationModeContext = createContext<PresentationModeContextType | undefined>(undefined);

const STORAGE_KEY = "presentation-mode";

export const PresentationModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Apply or remove the presentation-mode class on the root element
  useEffect(() => {
    const root = document.documentElement;
    if (enabled) {
      root.classList.add("presentation-mode");
    } else {
      root.classList.remove("presentation-mode");
    }
    try {
      localStorage.setItem(STORAGE_KEY, String(enabled));
    } catch {}
  }, [enabled]);

  const enable = useCallback(() => setEnabled(true), []);
  const disable = useCallback(() => setEnabled(false), []);
  const toggle = useCallback(() => setEnabled(prev => !prev), []);

  const value = useMemo(() => ({ enabled, enable, disable, toggle }), [enabled, enable, disable, toggle]);

  return (
    <PresentationModeContext.Provider value={value}>{children}</PresentationModeContext.Provider>
  );
};

export const usePresentationMode = () => {
  const ctx = useContext(PresentationModeContext);
  if (!ctx) throw new Error("usePresentationMode must be used within PresentationModeProvider");
  return ctx;
};
